import type { ReactNode } from 'react';
import { Popover } from '@bb/ui';
import type { ChannelMetricDefinition, PushMetricDefinition, PushMetricKey } from '../../../api/client.ts';

const INFLUENCED: ReadonlySet<string> = new Set<PushMetricKey>(['influenced_opens', 'influenced_open_rate', 'total_opens', 'total_open_rate']);

/** `384`, `98.7 %`; a rate without a denominator is a dash. */
export function formatPushMetric(format: PushMetricDefinition['format'], value: number | null): string {
  if (value === null) return '—';
  return format === 'percent' ? `${(value * 100).toFixed(1)} %` : value.toLocaleString('en-US');
}

/**
 * The definition of a push metric (BB-79) one click away: the tooltip text is the `formula` the API returns from the
 * same registry that computed the number (`PUSH_METRICS`), plus where the number is incomplete and the influenced-open
 * window. A popover, not a hover tooltip, so it works by keyboard and touch; it reuses the look of `@bb/ui` MetricTooltip,
 * which needs a count expression the push registry does not have. Card, in-app and other channel metrics of the
 * campaign analytics (BB-104) have the same shape without coverage.
 */
export function PushMetricTooltip({ definition, windowMinutes, children }: { definition: PushMetricDefinition | ChannelMetricDefinition; windowMinutes?: number; children?: ReactNode }) {
  const title = definition.title.en;
  return (
    <span className="bb-metric">
      {children}
      <Popover
        label={title}
        trigger={
          <button type="button" className="bb-metric__info" aria-label={`How "${title}" is calculated`}>
            <span aria-hidden="true">i</span>
          </button>
        }
      >
        <div className="bb-metric__details">
          <strong>{title}</strong>
          <p>{definition.formula.en}</p>
          {'coverage' in definition && definition.coverage && <p className="bb-metric__explanation">Coverage: {definition.coverage.en}</p>}
          {windowMinutes !== undefined && INFLUENCED.has(definition.key) && <p>Influenced-open window: {windowMinutes} min after the provider accepted the push.</p>}
          {definition.format === 'percent' && <p className="hint">Shown as a dash when the denominator is 0.</p>}
        </div>
      </Popover>
    </span>
  );
}
